// Die Landesequenz (Akt III): Anflug, Eintritt, Aufsetzen.
//
// **Sie ist reine Ausgabe** — dieselbe Regel wie bei der Heimflug-Sequenz.
// Die Kapsel steht im Spielstand, bevor die Kugel wächst; gebucht hat der Zug
// `land`, nicht diese Szene. Sie bucht nichts und schreibt keinen Zustand
// (FG-17). Wer sie überspringt, steht im selben Zustand wie danach.
//
// Wie ein Dialog hält sie die Welt an. Taste **und** Fingertipp überspringen,
// und beide Wege enden an **einem** Ausgang (`landing:done`).
//
// Alles prozedural: die Kugel ist dieselbe Textur wie im Feld, Glut und Boden
// sind Rechtecke. Kein Bild- und kein Ton-Asset (FG-10, FG-40).

import Phaser from 'phaser';

import { sfx } from '../audio/synth';
import { bus } from '../core/bus';
import { clamp } from '../core/math';
import { rngFrom } from '../core/rng';
import { planetById } from '../data/balancing';
import { planetTextureKey } from '../gfx/planet';
import { ensurePlanetTextures } from '../gfx/textures';

/** Die Abschnitte der Sequenz, in Sekunden ab Beginn. */
const ANFLUG = 2.2;
const EINTRITT = ANFLUG + 1.6;
const AUFSETZEN = EINTRITT + 2.4;

/** Wie viele Glutstreifen beim Eintritt vorbeiziehen. Einmal gezogen. */
const STREIFEN = 60;
const SAAT = 0x4c414e;

export interface LandingData {
  planet: string;
}

interface Streifen {
  x: number;
  laenge: number;
  tempo: number;
}

export class LandingScene extends Phaser.Scene {
  static readonly KEY = 'landing';

  private gfx!: Phaser.GameObjects.Graphics;
  private sphere!: Phaser.GameObjects.Image;
  private label!: Phaser.GameObjects.Text;

  /** Laufzeit — in `update` fortgeschrieben, nie im Zeichnen (FG-17). */
  private t = 0;
  private beendet = false;
  private planetId = '';
  private readonly streifen: Streifen[] = [];

  constructor() {
    super(LandingScene.KEY);
  }

  init(data: LandingData): void {
    this.planetId = data.planet;
    this.t = 0;
    this.beendet = false;
  }

  create(): void {
    const { width, height } = this.scale;
    ensurePlanetTextures(this);
    this.gfx = this.add.graphics().setDepth(1).setScrollFactor(0);
    this.sphere = this.add.image(width / 2, height / 2, planetTextureKey(this.planetId))
      .setDepth(0).setScrollFactor(0);
    const name = planetById(this.planetId)?.name ?? this.planetId;
    this.label = this.add.text(width / 2, height * 0.8, `Anflug auf ${name}`, {
      fontFamily: '"Lucida Console", monospace', fontSize: '15px', color: '#cfe8ff', align: 'center',
    }).setOrigin(0.5).setDepth(2).setScrollFactor(0);
    this.add.text(width / 2, height - 26, 'Leertaste oder Tippen überspringt', {
      fontFamily: '"Lucida Console", monospace', fontSize: '11px', color: '#6b7d8f',
    }).setOrigin(0.5).setDepth(2).setScrollFactor(0);

    this.streifen.length = 0;
    const rng = rngFrom(SAAT);
    for (let i = 0; i < STREIFEN; i++) {
      this.streifen.push({ x: rng() * width, laenge: 20 + rng() * 80, tempo: 0.6 + rng() * 0.8 });
    }

    this.input.keyboard?.on('keydown', () => this.finish());
    this.input.on('pointerdown', () => this.finish());
    sfx.warp();
  }

  override update(_time: number, delta: number): void {
    this.t += Math.min(0.05, delta / 1000);
    this.draw();
    if (this.t >= AUFSETZEN + 1.2) this.finish();
  }

  /** Nur lesen und zeichnen. Diese Methode weist nichts zu. */
  private draw(): void {
    const g = this.gfx;
    const { width, height } = this.scale;
    const planet = planetById(this.planetId);
    g.clear();
    g.fillStyle(0x020306, this.t < ANFLUG ? 0 : 1);
    if (this.t >= ANFLUG) g.fillRect(0, 0, width, height);

    // Anflug: die Kugel wächst, **ganzzahlig** — der Pixel-Look gilt auch hier.
    const quelle = this.textures.exists(planetTextureKey(this.planetId))
      ? (this.textures.get(planetTextureKey(this.planetId)).getSourceImage() as { width: number }).width : 1;
    const ziel = Math.max(1, Math.floor((Math.min(width, height) * 0.9) / quelle));
    const k = clamp(this.t / ANFLUG, 0, 1);
    this.sphere.setVisible(this.t < ANFLUG).setScale(Math.max(1, Math.round(1 + (ziel - 1) * k * k)));

    if (this.t >= ANFLUG && this.t < EINTRITT) {
      // Eintritt: Glut von unten nach oben, in der Farbe der Atmosphäre.
      const glut = parseInt((planet?.glow ?? '#ff8a3a').slice(1), 16);
      const p = (this.t - ANFLUG) / (EINTRITT - ANFLUG);
      for (const s of this.streifen) {
        const y = height - ((p * s.tempo * height * 2.2 + s.laenge * 3) % (height + s.laenge));
        g.fillStyle(glut, 0.75);
        g.fillRect(Math.round(s.x), Math.round(y), 2, Math.round(s.laenge));
      }
    } else if (this.t >= EINTRITT) {
      this.drawGround(g, width, height);
    }
    this.label.setText(this.zeile(planet?.name ?? this.planetId));
  }

  /** Boden, Horizont und die Kapsel, die aufsetzt. */
  private drawGround(g: Phaser.GameObjects.Graphics, width: number, height: number): void {
    const planet = planetById(this.planetId);
    const boden = parseInt((planet?.surface ?? '#8c6a4a').slice(1), 16);
    const dunkel = parseInt((planet?.dark ?? '#46301e').slice(1), 16);
    const horizont = Math.round(height * 0.62);
    g.fillStyle(dunkel, 1);
    g.fillRect(0, horizont, width, height - horizont);
    g.fillStyle(boden, 1);
    for (let x = 0; x < width; x += 8) {
      const h = ((x * 2654435761) >>> 0) % 7;
      g.fillRect(x, horizont - h, 8, h + 2);
    }
    const p = clamp((this.t - EINTRITT) / (AUFSETZEN - EINTRITT), 0, 1);
    const y = Math.round(height * 0.1 + (horizont - 22 - height * 0.1) * (1 - (1 - p) * (1 - p)));
    const x = Math.round(width / 2);
    g.fillStyle(0xd8d2c4, 1);
    g.fillRect(x - 10, y, 20, 18);
    g.fillStyle(0x6fe8ff, 1);
    g.fillRect(x - 4, y + 4, 8, 4);
    if (p < 1) {
      g.fillStyle(0xffb04a, 0.9);
      g.fillRect(x - 3, y + 18, 6, Math.round(6 + 10 * (1 - p)));
    } else {
      g.fillStyle(0x000000, 0.35);
      g.fillRect(x - 14, horizont - 2, 28, 3);
    }
  }

  private zeile(name: string): string {
    if (this.t < ANFLUG) return `Anflug auf ${name}`;
    if (this.t < EINTRITT) return 'Eintritt in die Atmosphäre';
    if (this.t < AUFSETZEN) return 'Bremsdüsen …';
    return `Gelandet. Die Kapsel auf ${name} steht.`;
  }

  /** Der eine Ausgang — Ablauf, Taste und Tipp treffen sich hier. */
  private finish(): void {
    if (this.beendet) return;
    this.beendet = true;
    this.scene.stop();
    bus.emit('landing:done', undefined);
  }
}
